<script setup lang="ts">
import { computed, onMounted, reactive, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { apiGet, apiPost, apiPut, apiSend } from "../api";
import { useToast } from "../stores/toast";

interface Category { id: string; slug: string; name: string; live: boolean; sortOrder: number; swatch?: string; productCount: number }

const route = useRoute();
const router = useRouter();
const toast = useToast();
const id = computed(() => route.params.id as string | undefined);
const isEdit = computed(() => !!id.value);

const form = reactive({ name: "", slug: "", swatch: "#F0563D", sortOrder: 0, live: false });
const productCount = ref(0);
const slugTouched = ref(false);
const saving = ref(false);
const deleting = ref(false);
const error = ref("");

const slugify = (s: string) =>
  s.toLowerCase().normalize("NFKD").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
function onName() {
  if (!isEdit.value && !slugTouched.value) form.slug = slugify(form.name);
}

onMounted(async () => {
  if (!isEdit.value) return;
  try {
    const d = await apiGet<{ items: Category[] }>("/categories");
    const c = d.items.find((x) => x.id === id.value);
    if (!c) { error.value = "Categorie niet gevonden"; return; }
    Object.assign(form, { name: c.name, slug: c.slug, swatch: c.swatch || "#F0563D", sortOrder: c.sortOrder, live: c.live });
    productCount.value = c.productCount;
    slugTouched.value = true;
  } catch (e) { error.value = `Laden mislukt: ${e}`; }
});

async function save() {
  error.value = "";
  if (!form.name.trim() || !form.slug.trim()) { error.value = "Naam en slug zijn verplicht"; return; }
  saving.value = true;
  const payload = { name: form.name.trim(), slug: form.slug.trim(), swatch: form.swatch, sortOrder: form.sortOrder, live: form.live };
  try {
    if (isEdit.value && id.value) await apiPut(`/categories/${id.value}`, payload);
    else await apiPost("/categories", payload);
    toast.show(isEdit.value ? "Categorie bijgewerkt" : "Categorie aangemaakt");
    router.push("/categorieen");
  } catch (e) { error.value = (e as Error).message; } finally { saving.value = false; }
}

async function remove() {
  if (!id.value) return;
  if (!confirm(`Categorie "${form.name}" verwijderen?`)) return;
  deleting.value = true;
  try {
    await apiSend("DELETE", `/categories/${id.value}`);
    toast.show("Categorie verwijderd");
    router.push("/categorieen");
  } catch (e) { error.value = (e as Error).message; } finally { deleting.value = false; }
}
</script>

<template>
  <div class="editor">
    <header class="ehead">
      <RouterLink to="/categorieen" class="back">←</RouterLink>
      <h1 class="title">{{ isEdit ? form.name || "Categorie bewerken" : "Nieuwe categorie" }}</h1>
      <div class="actions">
        <button v-if="isEdit" class="btn danger" :disabled="deleting" @click="remove">Verwijderen</button>
        <RouterLink to="/categorieen" class="btn ghost">Annuleren</RouterLink>
        <button class="btn primary" :disabled="saving || !form.name" @click="save">{{ saving ? "Opslaan…" : "Opslaan" }}</button>
      </div>
    </header>

    <p v-if="error" class="err">{{ error }}</p>

    <section class="card">
      <label class="fl">Naam</label>
      <input v-model="form.name" class="inp" placeholder="bv. Sokken" @input="onName" />

      <label class="fl">Slug</label>
      <input v-model="form.slug" class="inp mono" placeholder="sokken" @input="slugTouched = true" />
      <small class="hint">Gebruikt in de winkel-URL en als categorie-koppeling van producten.</small>

      <div class="row2">
        <div>
          <label class="fl">Kleur</label>
          <input v-model="form.swatch" type="color" class="colorpick" />
        </div>
        <div>
          <label class="fl">Volgorde</label>
          <input v-model.number="form.sortOrder" type="number" class="inp" />
        </div>
      </div>

      <label class="check"><input type="checkbox" v-model="form.live" /> Live in de winkel</label>

      <p v-if="isEdit && productCount > 0" class="note">
        {{ productCount }} product(en) in deze categorie. Verwijderen kan pas als de categorie leeg is.
      </p>
    </section>
  </div>
</template>

<style scoped>
.editor { max-width: 560px; }
.ehead { display: flex; align-items: center; gap: 14px; margin-bottom: 18px; }
.back { font-size: 22px; text-decoration: none; color: var(--ink); }
.title { font-family: var(--font-ui); font-weight: 700; font-size: 22px; margin: 0; }
.actions { margin-left: auto; display: flex; gap: 10px; }
.btn { border: 0; border-radius: 999px; padding: 10px 18px; font-weight: 700; font-size: 14px; cursor: pointer; text-decoration: none; }
.btn.ghost { background: var(--warm); color: var(--ink); }
.btn.primary { background: var(--coral); color: #fff; }
.btn.danger { background: var(--red-bg); color: var(--red); }
.btn:disabled { opacity: 0.6; cursor: not-allowed; }
.err { color: var(--red); background: var(--red-bg); padding: 10px 14px; border-radius: 10px; }
.card { background: var(--surface); border: 1px solid var(--hairline); border-radius: 14px; padding: 18px; }
.fl { display: block; font-size: 12px; font-weight: 600; color: var(--muted); text-transform: uppercase; letter-spacing: 0.04em; margin: 12px 0 6px; }
.fl:first-child { margin-top: 0; }
.inp { width: 100%; padding: 10px 12px; border: 1px solid var(--border-input); border-radius: 10px; background: var(--surface); font-size: 14px; font-family: inherit; }
.inp.mono { font-family: monospace; }
.hint { display: block; color: var(--muted); font-size: 12px; margin-top: 4px; }
.row2 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; align-items: end; }
.colorpick { width: 100%; height: 42px; border: 1px solid var(--border-input); border-radius: 10px; padding: 2px; background: var(--surface); }
.check { display: flex; align-items: center; gap: 8px; margin-top: 16px; font-size: 14px; font-weight: 600; }
.note { color: var(--muted); font-size: 13px; margin: 14px 0 0; }
</style>
